import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { FileText, ArrowLeft } from "lucide-react";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import TemplateForm from "@/components/document-templates/template-form";

export const Route = createFileRoute(
  "/_authenticated/documents/templates/$id/edit",
)({
  component: EditTemplateRoute,
});

function EditTemplateRoute() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const isValidId = /^[a-z0-9]{20,40}$/.test(id);
  const template = useQuery(
    api.companies.documents.templates.queries.getTemplateById,
    isValidId ? { id: id as Id<"document_templates"> } : "skip",
  );

  if (template === undefined) {
    return (
      <RouteGuardPermissions
        permissions={[{ module: "document_templates", actions: ["read", "update"] }]}
      >
        <Card className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2 shadow-sm">
          <CardHeader className="p-0! flex justify-between items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1 p-0">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-7 w-24" />
            </div>
            <div className="p-0 flex gap-1 items-center">
              <Skeleton className="h-7 w-7" />
              <Skeleton className="h-7 w-7" />
            </div>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-3 px-0">
            <div className="flex min-h-0 flex-1 gap-2">
              <Skeleton className="h-full min-h-100 flex-1" />
              <Skeleton className="h-full w-72" />
            </div>
          </CardContent>
        </Card>
      </RouteGuardPermissions>
    );
  }

  if (template === null) {
    return (
      <RouteGuardPermissions
        permissions={[{ module: "document_templates", actions: ["read", "update"] }]}
      >
        <div className="flex flex-col items-center justify-center gap-4 py-20">
          <div className="rounded-full bg-muted p-4">
            <FileText className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold">Plantilla no encontrada</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Esta plantilla no existe o fue eliminada.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: "/documents" })}
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Volver a Documentos
          </Button>
        </div>
      </RouteGuardPermissions>
    );
  }

  return (
    <RouteGuardPermissions
      permissions={[{ module: "document_templates", actions: ["read", "update"] }]}
    >
      <TemplateForm
        key={template._id}
        template={template}
        onCancel={() => navigate({ to: "/documents" })}
        onSuccess={() => navigate({ to: "/documents" })}
      />
    </RouteGuardPermissions>
  );
}
